const STATUS_TO_CODE = {
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    // The API's rate-limit 429s carry the full envelope already; this fallback
    // covers envelope-less 429s (e.g. minted by nginx) so the frontends can
    // still key their translated copy off the code instead of echoing English.
    429: 'RATE_LIMITED',
};
/**
 * Thin fetch wrapper shared by every frontend. All API calls funnel through
 * `request()` so the bearer-token attachment, JSON-envelope unwrapping, and
 * legacy-error normalisation live in exactly one place.
 */
export class ApiClient {
    baseUrl;
    token = null;
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }
    setToken(token) {
        this.token = token;
    }
    async request(endpoint, options = {}) {
        const headers = {
            ...options.headers,
        };
        // Only set Content-Type when we actually send a body — DELETE/GET with no
        // body would otherwise trigger Fastify's FST_ERR_CTP_EMPTY_JSON_BODY.
        // FormData bodies must set their own multipart boundary; never override.
        const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
        if (options.body !== undefined && options.body !== null && !isFormData) {
            headers['Content-Type'] ??= 'application/json';
        }
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers,
            });
            // 204 No Content has no body to parse.
            const text = response.status === 204 ? '' : await response.text();
            const body = text ? safeJsonParse(text) : null;
            // New routes already return the `{ success, ... }` envelope — pass through.
            if (body && typeof body === 'object' && 'success' in body) {
                return body;
            }
            if (!response.ok) {
                const fallbackCode = STATUS_TO_CODE[response.status] ?? 'UNKNOWN_ERROR';
                const errBody = (body ?? {});
                return {
                    success: false,
                    error: {
                        code: errBody.code ?? fallbackCode,
                        message: errBody.message ?? 'An unknown error occurred',
                    },
                };
            }
            // Legacy routes return raw data — wrap it.
            return { success: true, data: body };
        }
        catch (error) {
            return {
                success: false,
                error: {
                    code: 'NETWORK_ERROR',
                    message: error instanceof Error ? error.message : 'Network error',
                },
            };
        }
    }
    get(endpoint) {
        return this.request(endpoint, { method: 'GET' });
    }
    post(endpoint, body) {
        return this.request(endpoint, {
            method: 'POST',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }
    put(endpoint, body) {
        return this.request(endpoint, {
            method: 'PUT',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }
    patch(endpoint, body) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }
    delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }
    /** Multipart POST — body must be a FormData. Browser sets the boundary. */
    postFormData(endpoint, body) {
        return this.request(endpoint, { method: 'POST', body });
    }
}
function safeJsonParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=index.js.map